import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  getAgents, createAgent, deleteAgent, updateAgent,
  installAgentSkill, putAgentSkillCredentials, updateListenerSpec,
  installListenerFromCatalog, reloadService,
  fetchCatalog, testCatalogConnection,
} from '../data';
import {
  Mail, MessageSquare, Globe, Clock, Rss, Webhook,
  Send, Database, Search, FileText, ArrowRight, ArrowLeft,
  Check, Sparkles, X, ChevronRight, Loader2, AlertCircle, Info,
  Calendar, CalendarPlus,
} from 'lucide-react';
import './Portal.css';
import { LISTENER_SETUP_HELP } from '../listener_setup_help.jsx';
import QuotaBanner from '../components/QuotaBanner';

// Map catalog item ids to icons so the wizard can render them.
const ICONS = {
  email_inbox: Mail, slack_messages: MessageSquare,
  database_read: Database, webhook_incoming: Webhook, scheduled_trigger: Clock,
  rss_feed: Rss, google_calendar: Calendar,
  email_manager: Send, slack_send: MessageSquare, database_query: Database,
  http_request: Globe, web_search: Search, file_read_write: FileText,
  create_calendar_event: CalendarPlus, read_calendar: Calendar,
};
const iconFor = (id) => ICONS[id] || Sparkles;

// ----- keyword matching (mirrors catalog.suggest on the backend) -----------

function defaultMode(item) {
  const modes = item.modes || [];
  if (!modes.length) return null;
  const d = modes.find(m => m.default);
  return d ? d.id : modes[0].id;
}

function pickMode(item, text) {
  // Mirrors agent/catalog.py::pick_mode — on a tie, an explicitly-matched
  // non-default mode beats the default (e.g. "draft a reply" -> draft).
  const modes = item.modes || [];
  if (!modes.length) return null;
  const def = defaultMode(item);
  const counts = modes.map(m => [m.id, (m.keywords || []).filter(kw => kw && text.includes(kw.toLowerCase())).length]);
  const maxHits = counts.reduce((a, [, h]) => Math.max(a, h), 0);
  if (maxHits === 0) return def;
  const leaders = counts.filter(([, h]) => h === maxHits).map(([id]) => id);
  if (leaders.length === 1) return leaders[0];
  const nonDefault = leaders.filter(id => id !== def);
  return nonDefault.length ? nonDefault[0] : def;
}

function computeSuggestions(text, catalog) {
  const t = (text || '').toLowerCase();
  const match = (item) => (item.keywords || []).some(kw => kw && t.includes(kw.toLowerCase()));
  const listeners = {}, skills = {};
  (catalog.listeners || []).forEach(i => { if (match(i)) listeners[i.id] = { mode: pickMode(i, t) }; });
  (catalog.skills || []).forEach(i => { if (match(i)) skills[i.id] = { mode: pickMode(i, t) }; });
  return { listeners, skills };
}

const modeLabel = (item, modeId) =>
  (item.modes || []).find(m => m.id === modeId)?.label || modeId || '';

// Compact "MM/DD" for the row metadata, or "Today" if the agent was created
// within the last 24 hours. Returns empty string for missing/invalid dates so
// the row's metadata column just omits the field rather than printing "—".
function formatAddedDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diffHrs = (Date.now() - d.getTime()) / 36e5;
  if (diffHrs < 24) return 'today';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}·${dd}`;
}

// ---------------------------------------------------------------------------

const STEPS = [
  { title: 'Name', subtitle: 'What should we call it?' },
  { title: 'Describe', subtitle: 'What should it do?' },
  { title: 'Listeners', subtitle: 'What triggers it?' },
  { title: 'Skills', subtitle: 'What can it do?' },
  { title: 'Credentials', subtitle: 'Connect your accounts' },
  { title: 'Launch', subtitle: 'Ready to go' },
];

function OnboardingWizard({ onClose, onComplete }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [agentName, setAgentName] = useState('');
  const [description, setDescription] = useState('');
  const [catalog, setCatalog] = useState({ listeners: [], skills: [] });
  const [selListeners, setSelListeners] = useState({}); // id -> {mode}
  const [selSkills, setSelSkills] = useState({});        // id -> {mode}
  const [creds, setCreds] = useState({});                // itemId -> {NAME: value}
  const [tests, setTests] = useState({});                // itemId -> {state, message}
  const [touchedCaps, setTouchedCaps] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [needsUpgrade, setNeedsUpgrade] = useState(false);
  const [helpItemId, setHelpItemId] = useState(null);

  useEffect(() => {
    fetchCatalog().then(setCatalog).catch(() => {});
  }, []);

  const listenerById = (id) => catalog.listeners.find(i => i.id === id);
  const skillById = (id) => catalog.skills.find(i => i.id === id);

  const applySuggestions = () => {
    const { listeners, skills } = computeSuggestions(`${agentName} ${description}`, catalog);
    setSelListeners(listeners);
    setSelSkills(skills);
    setTouchedCaps(true);
  };

  const goNext = () => {
    // Auto-detect listeners + skills the first time we leave the description step.
    if (step === 1 && !touchedCaps) applySuggestions();
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };
  const goBack = () => setStep(s => Math.max(s - 1, 0));

  const canProceed = () => {
    if (step === 0) return agentName.trim().length > 0;
    return true;
  };

  const toggleListener = (id) => setSelListeners(prev => {
    const next = { ...prev };
    if (next[id]) delete next[id];
    else next[id] = { mode: defaultMode(listenerById(id)) };
    return next;
  });
  const toggleSkill = (id) => setSelSkills(prev => {
    const next = { ...prev };
    if (next[id]) delete next[id];
    else next[id] = { mode: defaultMode(skillById(id)) };
    return next;
  });
  const setListenerMode = (id, mode) => setSelListeners(prev => ({ ...prev, [id]: { ...prev[id], mode } }));
  const setSkillMode = (id, mode) => setSelSkills(prev => ({ ...prev, [id]: { ...prev[id], mode } }));

  const setCred = (itemId, name, value) =>
    setCreds(prev => ({ ...prev, [itemId]: { ...prev[itemId], [name]: value } }));

  // Items that need credentials, in display order (listeners then skills).
  const credentialTargets = () => {
    const out = [];
    Object.keys(selListeners).forEach(id => {
      const item = listenerById(id);
      if (item && (item.credentials || []).length) out.push({ kind: 'listener', item });
    });
    Object.keys(selSkills).forEach(id => {
      const item = skillById(id);
      if (item && (item.credentials || []).length) out.push({ kind: 'skill', item });
    });
    return out;
  };

  const handleTest = async (kind, item) => {
    setTests(prev => ({ ...prev, [item.id]: { state: 'testing' } }));
    try {
      const res = await testCatalogConnection({
        itemId: item.id, kind, credentials: creds[item.id] || {},
      });
      setTests(prev => ({
        ...prev,
        [item.id]: { state: res.ok ? 'ok' : 'fail', message: res.message || '' },
      }));
    } catch (e) {
      setTests(prev => ({ ...prev, [item.id]: { state: 'fail', message: e.message || 'Test failed' } }));
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    setNeedsUpgrade(false);
    try {
      await onComplete({
        name: agentName.trim(),
        description: description.trim(),
        listeners: Object.entries(selListeners).map(([id, v]) => ({
          id, mode: v.mode, credentials: creds[id] || {},
        })),
        skills: Object.entries(selSkills).map(([id, v]) => ({
          id, mode: v.mode, credentials: creds[id] || {},
        })),
      });
    } catch (err) {
      if (err.status === 402) {
        setError(err.data?.error || err.detail || 'Agent limit reached on your plan.');
        setNeedsUpgrade(true);
      } else {
        setError(err.message || 'Failed to create agent');
      }
      setCreating(false);
    }
  };

  const renderCard = (item, selected, mode, onToggle, onMode) => {
    const Icon = iconFor(item.id);
    return (
      <div key={item.id} className={`wizard-card ${selected ? 'selected' : ''}`} onClick={() => onToggle(item.id)}>
        <div className="wizard-card-icon"><Icon size={18} /></div>
        <div className="wizard-card-info">
          <h4>{item.name}</h4>
          <p>{item.description}</p>
          {selected && (item.modes || []).length > 0 && (
            <select
              className="wizard-mode-select"
              value={mode || ''}
              onClick={e => e.stopPropagation()}
              onChange={e => onMode(item.id, e.target.value)}
            >
              {item.modes.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          )}
        </div>
        <div className="wizard-card-check">{selected && <Check size={14} />}</div>
      </div>
    );
  };

  return (
    <motion.div className="wizard-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div
        className="wizard"
        initial={{ opacity: 0, y: 30, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 30, scale: 0.97 }}
        transition={{ type: 'spring', damping: 30, stiffness: 400 }}
        onClick={e => e.stopPropagation()}
      >
        <button className="wizard-close" onClick={onClose}><X size={16} /></button>

        <div className="wizard-progress">
          {STEPS.map((s, i) => (
            <div key={i} className={`wizard-progress-step ${i <= step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
              <div className="wizard-progress-dot">{i < step ? <Check size={10} /> : <span>{i + 1}</span>}</div>
              <span className="wizard-progress-label">{s.title}</span>
              {i < STEPS.length - 1 && <div className="wizard-progress-line" />}
            </div>
          ))}
        </div>

        <div className="wizard-body">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="s0" className="wizard-step" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <div className="wizard-step-header"><h2>Name your agent</h2><p>Give it a short, memorable name.</p></div>
                <div className="wizard-form">
                  <div className="wizard-field">
                    <label>Agent Name</label>
                    <input
                      type="text"
                      placeholder="e.g., Support Bot, Sales Assistant, Data Monitor"
                      value={agentName}
                      onChange={e => setAgentName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && canProceed()) goNext(); }}
                      autoFocus
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div key="s1" className="wizard-step" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <div className="wizard-step-header">
                  <h2>What should it do?</h2>
                  <p>Describe its job, goals, and how it should behave. We'll use this to pick the right inputs and skills.</p>
                </div>
                <div className="wizard-form">
                  <div className="wizard-field">
                    <label>Description & tasks</label>
                    <textarea
                      className="wizard-textarea"
                      rows={6}
                      placeholder="e.g., Read unread customer emails and draft a polite reply when they ask about pricing or refunds. Escalate anything legal."
                      value={description}
                      onChange={e => { setDescription(e.target.value); setTouchedCaps(false); }}
                      autoFocus
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" className="wizard-step" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <div className="wizard-step-header">
                  <h2>Listeners</h2>
                  <p>What should trigger your agent? Auto-selected from your description — adjust anything you like.</p>
                </div>
                <div className="wizard-grid">
                  {catalog.listeners.map(item =>
                    renderCard(item, !!selListeners[item.id], selListeners[item.id]?.mode, toggleListener, setListenerMode))}
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="s3" className="wizard-step" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <div className="wizard-step-header">
                  <h2>Skills</h2>
                  <p>What actions can your agent take? The mode dropdown picks how each skill behaves.</p>
                </div>
                <div className="wizard-grid">
                  {catalog.skills.map(item =>
                    renderCard(item, !!selSkills[item.id], selSkills[item.id]?.mode, toggleSkill, setSkillMode))}
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div key="s4" className="wizard-step" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <div className="wizard-step-header">
                  <h2>Connect your accounts</h2>
                  <p>Enter credentials for what you selected, then test each connection.</p>
                </div>
                {credentialTargets().length === 0 ? (
                  <div className="wizard-summary-empty">Nothing to connect — your selections need no credentials.</div>
                ) : (
                  <div className="wizard-creds">
                    {credentialTargets().map(({ kind, item }) => {
                      const t = tests[item.id] || {};
                      return (
                        <div key={item.id} className="wizard-cred-group">
                          <div className="wizard-cred-head">
                            {(() => { const Icon = iconFor(item.id); return <Icon size={15} />; })()}
                            <span>{item.name}</span>
                            {LISTENER_SETUP_HELP[item.id] && (
                              <button
                                type="button"
                                className="btn btn-ghost btn-sm wizard-cred-help"
                                onClick={() => setHelpItemId(item.id)}
                                title="Setup guide"
                              >
                                <Info size={13} /> Setup guide
                              </button>
                            )}
                          </div>
                          {item.credentials.map(c => (
                            <div key={c.name} className="wizard-field">
                              <label>{c.label}</label>
                              <input
                                type={c.type === 'password' ? 'password' : 'text'}
                                placeholder={c.placeholder || ''}
                                value={(creds[item.id]?.[c.name]) || ''}
                                onChange={e => setCred(item.id, c.name, e.target.value)}
                              />
                            </div>
                          ))}
                          <div className="wizard-cred-test">
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              disabled={t.state === 'testing'}
                              onClick={() => handleTest(kind, item)}
                            >
                              {t.state === 'testing' ? <Loader2 size={13} className="spin" /> : null} Test connection
                            </button>
                            {t.state === 'ok' && <span className="cred-status ok"><Check size={13} /> {t.message}</span>}
                            {t.state === 'fail' && <span className="cred-status fail"><AlertCircle size={13} /> {t.message}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            )}

            {step === 5 && (
              <motion.div key="s5" className="wizard-step" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <div className="wizard-step-header"><h2>Ready to launch</h2><p>Here's what we're setting up:</p></div>
                <div className="wizard-summary">
                  <div className="wizard-summary-item"><Sparkles size={14} /><span><strong>{agentName}</strong></span></div>
                  {description && <div className="wizard-summary-item"><ChevronRight size={14} /><span>{description}</span></div>}
                  {Object.keys(selListeners).length > 0 && (
                    <div className="wizard-summary-section">
                      <h4>Inputs</h4>
                      {Object.entries(selListeners).map(([id, v]) => {
                        const item = listenerById(id); if (!item) return null;
                        const ml = modeLabel(item, v.mode);
                        return <div key={id} className="wizard-summary-tag">{item.name}{ml ? ` · ${ml}` : ''}</div>;
                      })}
                    </div>
                  )}
                  {Object.keys(selSkills).length > 0 && (
                    <div className="wizard-summary-section">
                      <h4>Skills</h4>
                      {Object.entries(selSkills).map(([id, v]) => {
                        const item = skillById(id); if (!item) return null;
                        const ml = modeLabel(item, v.mode);
                        return <div key={id} className="wizard-summary-tag">{item.name}{ml ? ` · ${ml}` : ''}</div>;
                      })}
                    </div>
                  )}
                  {Object.keys(selListeners).length === 0 && Object.keys(selSkills).length === 0 && (
                    <div className="wizard-summary-empty">No listeners or skills selected — you can add them later from the Connections page.</div>
                  )}
                </div>
                {error && (
                  <div className="wizard-error">
                    <span>{error}</span>
                    {needsUpgrade && (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => { onClose(); navigate('/pricing'); }}
                      >
                        Upgrade
                      </button>
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="wizard-footer">
          <div className="wizard-footer-left">
            {step > 0 && <button className="btn btn-ghost" onClick={goBack}><ArrowLeft size={14} /> Back</button>}
          </div>
          <div className="wizard-footer-right">
            {step < STEPS.length - 1 ? (
              <button className="btn btn-primary" onClick={goNext} disabled={!canProceed()}>
                Continue <ArrowRight size={14} />
              </button>
            ) : (
              <button className="btn btn-primary btn-launch" onClick={handleCreate} disabled={creating}>
                {creating ? 'Creating...' : 'Launch Agent'} <Sparkles size={14} />
              </button>
            )}
          </div>
        </div>
      </motion.div>
      {helpItemId && LISTENER_SETUP_HELP[helpItemId] && (
        <div className="modal-overlay" onClick={() => setHelpItemId(null)} role="dialog" aria-modal="true">
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{LISTENER_SETUP_HELP[helpItemId].title}</span>
              <button type="button" className="modal-close" onClick={() => setHelpItemId(null)} aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="modal-body listener-setup-help">{LISTENER_SETUP_HELP[helpItemId].body}</div>
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function Portal() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);

  const { user, logOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    loadAgents();
  }, [user]);

  const loadAgents = async () => {
    if (!user) return;
    try {
      const agentList = await getAgents(user.uid);
      setAgents(agentList);
    } catch (err) {
      console.error('Failed to load agents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleWizardComplete = async ({ name, description, listeners, skills }) => {
    const agentId = await createAgent(user.uid, {
      name,
      type: 'custom',
      role: '',
      industry: 'Custom Agent',
      objective: description || '',
      description: description || '',
      successDefinition: '',
      doGuidelines: '',
      avoidGuidelines: '',
      toneFormality: 50,
      toneEnthusiasm: 50,
      escalationWhen: '',
      escalationChannel: 'none',
      escalationWho: '',
      escalationContext: '',
      escalationSLA: '',
    });

    // Build listener specs from user selections only -- no heartbeat by default.
    const agentListeners = [];

    for (const sel of listeners) {
      const item = catalogListenerType(sel.id);
      const listenerId = `${sel.id}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      agentListeners.push({
        id: listenerId,
        name: sel.id,
        type: item.type,
        description: '',
        settings: { poll_interval: 60, mode: sel.mode || '' },
        user_params: sel.credentials || {},
        enabled: true,
        fromCatalog: true,
        catalog_item_id: sel.id,
        mode: sel.mode || '',
        addedAt: new Date().toISOString(),
      });
    }

    await updateAgent(agentId, { listeners: agentListeners });

    // Materialize listener code from the catalog.
    for (const spec of agentListeners) {
      if (spec.catalog_item_id) {
        try { await installListenerFromCatalog(agentId, spec.id, spec.catalog_item_id); }
        catch (e) { console.error('listener catalog install failed:', e); }
      }
    }

    // Install skills (with their chosen mode) and save their credentials.
    for (const sel of skills) {
      try {
        await installAgentSkill(agentId, { source: 'catalog', item_id: sel.id, mode: sel.mode || '' });
        if (sel.credentials && Object.keys(sel.credentials).length) {
          await putAgentSkillCredentials(agentId, sel.id, { credentials: sel.credentials });
        }
      } catch (e) {
        console.error(`Failed to install skill ${sel.id}:`, e);
      }
    }

    await reloadService().catch(() => {});
    navigate(`/portal/agent/${agentId}/builder`);
  };

  const handleDeleteAgent = async (agentId) => {
    if (!confirm('Delete this agent?')) return;
    try {
      await deleteAgent(agentId);
      setAgents(agents.filter(a => a.id !== agentId));
    } catch (err) {
      console.error('Failed to delete agent:', err);
    }
  };

  const handleSignOut = async () => {
    await logOut();
    navigate('/');
  };

  return (
    <div className="portal">
      <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
        {theme === 'dark' ? '○' : '●'}
      </button>

      <header className="portal-header">
        <div className="portal-header-left">
          <span className="portal-wordmark" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>AEGENT</span>
        </div>
        <div className="portal-header-right">
          <span className="portal-email">{user?.email}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/settings')}>Settings</button>
          <button className="btn btn-ghost btn-sm" onClick={handleSignOut}>Sign Out</button>
        </div>
      </header>

      <main className="portal-main">
        <QuotaBanner />
        {loading ? (
          <div className="portal-loading"><div className="loading-spinner"></div></div>
        ) : (
          <>
            <section className="board-head">
              <div className="board-head-row">
                <h1 className="board-title">Agents</h1>
                <button className="board-new" onClick={() => setShowWizard(true)}>
                  <span className="board-new-plus">+</span> New agent
                </button>
              </div>
              <div className="board-meta">
                <span className="board-meta-cell">
                  <span className="board-meta-num">{String(agents.filter(a => a.isActive !== false).length).padStart(2, '0')}</span>
                  <span className="board-meta-label">Live</span>
                </span>
                <span className="board-meta-sep" aria-hidden="true">/</span>
                <span className="board-meta-cell">
                  <span className="board-meta-num">{String(agents.length).padStart(2, '0')}</span>
                  <span className="board-meta-label">Total</span>
                </span>
                <span className="board-meta-spacer" aria-hidden="true" />
                <span className="board-meta-cell board-meta-cell--end">
                  <span className="board-meta-label">Operator</span>
                  <span className="board-meta-num board-meta-num--sm">{user?.email?.split('@')[0] || '—'}</span>
                </span>
              </div>
            </section>

            {agents.length === 0 ? (
              <div className="board-empty">
                <h2 className="board-empty-title">Stand one up</h2>
                <p className="board-empty-text">
                  An agent listens for events — email, Slack, a webhook, a schedule —
                  then acts on them. Pick what it watches and what it can do.
                </p>
                <button className="board-empty-cta" onClick={() => setShowWizard(true)}>
                  Create agent <ArrowRight size={14} />
                </button>
              </div>
            ) : (
              <div className="board-grid">
                {agents.map((agent, i) => {
                  const inactive = agent.isActive === false;
                  const idx = String(i).padStart(2, '0');
                  const created = formatAddedDate(agent.createdAt);
                  return (
                    <motion.article
                      key={agent.id}
                      className={`board-card ${inactive ? 'is-inactive' : 'is-live'}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      onClick={() => navigate(`/portal/agent/${agent.id}/builder`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/portal/agent/${agent.id}/builder`); }}
                    >
                      <span className="board-card-mark" aria-hidden="true" />
                      <header className="board-card-top">
                        <span className="board-card-idx">{idx}</span>
                        <button
                          className="board-card-delete"
                          onClick={(e) => { e.stopPropagation(); handleDeleteAgent(agent.id); }}
                          aria-label={`Delete ${agent.name}`}
                        >
                          <X size={13} />
                        </button>
                      </header>
                      <h3 className="board-card-name">{agent.name || 'Unnamed agent'}</h3>
                      <p className="board-card-desc">
                        {agent.description || agent.role || agent.industry || 'No description set.'}
                      </p>
                      <footer className="board-card-foot">
                        <span className="board-card-status">
                          <span className="board-card-lamp" aria-hidden="true" />
                          {inactive ? 'Standby' : 'Live'}
                        </span>
                        {created && <span className="board-card-added">{created}</span>}
                      </footer>
                    </motion.article>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>

      <AnimatePresence>
        {showWizard && <OnboardingWizard onClose={() => setShowWizard(false)} onComplete={handleWizardComplete} />}
      </AnimatePresence>
    </div>
  );
}

// Map a catalog listener id to its listener type. We keep a tiny static map so
// the create handler doesn't need the full catalog in scope; the backend is the
// source of truth and re-derives type on install.
const LISTENER_TYPES = {
  email_inbox: 'polling', slack_messages: 'polling',
  database_read: 'polling', rss_feed: 'polling', google_calendar: 'polling',
  webhook_incoming: 'webhook', twilio_messages: 'webhook',
  scheduled_trigger: 'cron',
};
function catalogListenerType(id) {
  return { type: LISTENER_TYPES[id] || 'polling' };
}
